import { Fragment, Slice } from '@atlaskit/editor-prosemirror/model';
import type { EditorState, Transaction } from '@atlaskit/editor-prosemirror/state';
import { closeHistory } from '@atlaskit/prosemirror-history/closeHistory';

import { configItemUsesRovo } from '../../../ui/components/SessionUI/utils/checkMigrationStatus';
import type { StepName } from '../../../ui/utils/analytics/analyticsFlowTypes';
import { startWatchingAnnotationChanges } from '../../ai-annotation-watchmen/transforms';
import { aiSessionContextPluginKey } from '../../ai-session-context/ai-session-context-plugin-key';
import { aiExperienceDecorationPluginKey } from '../../decoration/decoration-plugin-key';
import { initPositions } from '../transforms';
import { validatePositions } from '../utils/positionValidation';

export type FireAnalyticsStep = (step: StepName, targetStep?: StepName) => void;

/**
 * Builds a transaction to initialize streaming decorations and state.
 * Returns null if:
 * - No positions or config item available
 * - Waiting for orchestrator to set correct positions (deferred mount)
 * - Unable to slice the original user content
 */
export function buildMountTransaction(
	state: EditorState,
	fireAnalyticsStep: FireAnalyticsStep,
): Transaction | null {
	const decorationPluginState = aiExperienceDecorationPluginKey.getState(state);
	const { positions, configItem, lastTriggeredFrom } = decorationPluginState ?? {};

	if (!positions || !configItem) {
		return null;
	}

	// Defer mounting for rovo prompts until we have the correct positions.
	// Exception: fallback free-gen (userInput is set) already has correct positions
	// from the initial decoration, so we can mount immediately.
	const sessionState = aiSessionContextPluginKey.getState(state);
	const isFallbackFreeGen = sessionState?.userInput !== undefined;

	if (
		configItemUsesRovo(configItem.key) &&
		lastTriggeredFrom &&
		lastTriggeredFrom !== 'streamingApi' &&
		!isFallbackFreeGen
	) {
		return null;
	}
	const [from, to] = positions;

	const validation = validatePositions(state, from, to);

	const docSize = state.doc.content.size;
	const isEmptySelection = from === to;
	const shouldReplaceDoc = isEmptySelection && !configItem?.insertOnEmptySelection;

	const [start, end] = shouldReplaceDoc
		? [1, Math.max(1, docSize - 1)]
		: [validation.sanitized.startPos, validation.sanitized.endPos];

	const showTelepointer = !configItem?.streamsMarkdown;
	const showHighlight = !configItem?.streamsMarkdown;
	const showAIContentHighlight = !shouldReplaceDoc;

	// Prefer the exact node a `replaceNode` command recorded (via
	// `recordOriginalNode`) to build `originalUserContentSlice`. `doc.slice` can
	// return a different shape (e.g. listItem children of a bulletList). Falls
	// back to the doc-range slice when no node was recorded or for a whole-doc
	// replace.
	const recordedOriginalNode = shouldReplaceDoc ? undefined : sessionState?.replaceNodeOriginalNode;

	let originalUserContentSlice;
	if (recordedOriginalNode) {
		originalUserContentSlice = new Slice(Fragment.from(recordedOriginalNode), 0, 0);
	} else {
		try {
			originalUserContentSlice = state.doc.slice(start, end);
		} catch {
			return null;
		}
	}

	let telepointerPos = start;
	try {
		const $start = state.doc.resolve(start);
		telepointerPos = $start.pos;
		let node = $start.node()?.firstChild;
		while (node && node.firstChild && !node.isLeaf) {
			telepointerPos += 1;
			node = node.firstChild;
		}
	} catch {
		telepointerPos = start;
	}

	let tr = state.tr;
	tr = closeHistory(tr);

	tr = startWatchingAnnotationChanges({ from: start, to: end })(tr);

	tr = initPositions({
		start,
		end,
		showHighlight: showHighlight && decorationPluginState?.withStreamingHighlight !== false,
		showAIContentHighlight,
		showTelepointer,
		telepointerPos,
		originalUserContentSlice,
		streamStartPos: start,
		sessionId: decorationPluginState?.currentSessionId,
	})(tr);

	fireAnalyticsStep('invoke', 'loading');

	return tr;
}
